import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useFavourites } from '../context/FavouritesContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { 
  Sun, Moon, ShoppingCart, Heart, User, LogOut, 
  Store, MessageCircle, Package, Menu, Search, MessagesSquare, Bell
} from 'lucide-react';
import { NotificationDropdown } from './NotificationDropdown';
import { Wordmark } from './Wordmark';

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const { count: favouriteCount } = useFavourites();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const NavLinks = ({ mobile = false }) => {
    const linkClass = mobile
      ? "lowercase font-display font-bold text-lg text-text-primary hover:text-primary transition-colors"
      : "lowercase font-display font-bold text-foreground/75 hover:text-primary transition-colors";
    return (
    <>
      <Link 
        to="/browse" 
        className={linkClass}
        onClick={() => mobile && setMobileOpen(false)}
        data-testid="nav-browse"
      >
        Browse
      </Link>
      {user?.is_creator && (
        <Link 
          to="/community" 
          className={`${linkClass} flex items-center gap-1`}
          onClick={() => mobile && setMobileOpen(false)}
          data-testid="nav-community"
        >
          <MessagesSquare className="w-4 h-4" />
          Community
        </Link>
      )}
      <Link 
        to="/browse?category=crochet" 
        className={linkClass}
        onClick={() => mobile && setMobileOpen(false)}
      >
        Crochet
      </Link>
      <Link 
        to="/browse?category=stickers" 
        className={linkClass}
        onClick={() => mobile && setMobileOpen(false)}
      >
        Stickers
      </Link>
      <Link 
        to="/browse?category=art" 
        className={linkClass}
        onClick={() => mobile && setMobileOpen(false)}
      >
        Art
      </Link>
      <Link 
        to="/browse?category=keyrings" 
        className={linkClass}
        onClick={() => mobile && setMobileOpen(false)}
      >
        Keyrings
      </Link>
    </>
    );
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-nav/95 backdrop-blur supports-[backdrop-filter]:bg-nav/85 text-nav-foreground">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <Wordmark />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <NavLinks />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full text-foreground hover:bg-foreground/10 hover:text-foreground"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>

            <Link to="/search">
              <Button variant="ghost" size="icon" className="rounded-full text-foreground hover:bg-foreground/10 hover:text-foreground" data-testid="nav-search">
                <Search className="w-5 h-5" />
              </Button>
            </Link>

            {user && (
              <>
                <NotificationDropdown />
                
                <Link to="/favourites" className="relative">
                  <Button variant="ghost" size="icon" className="rounded-full text-foreground hover:bg-foreground/10 hover:text-foreground" data-testid="nav-favourites">
                    <Heart className="w-5 h-5" />
                    {favouriteCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-bold border-2 border-background" data-testid="favourites-count">
                        {favouriteCount}
                      </span>
                    )}
                  </Button>
                </Link>

                <Link to="/cart" className="relative">
                  <Button variant="ghost" size="icon" className="rounded-full text-foreground hover:bg-foreground/10 hover:text-foreground" data-testid="nav-cart">
                    <ShoppingCart className="w-5 h-5" />
                    {itemCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-bold border-2 border-background">
                        {itemCount}
                      </span>
                    )}
                  </Button>
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full text-foreground hover:bg-foreground/10 hover:text-foreground" data-testid="nav-user-menu">
                      <User className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="font-semibold">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="menu-profile">
                        <User className="w-4 h-4" /> Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/orders" className="flex items-center gap-2 cursor-pointer" data-testid="menu-orders">
                        <Package className="w-4 h-4" /> Orders
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/favourites" className="flex items-center gap-2 cursor-pointer" data-testid="menu-saved">
                        <Heart className="w-4 h-4" /> Saved for Later
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/messages" className="flex items-center gap-2 cursor-pointer" data-testid="menu-messages">
                        <MessageCircle className="w-4 h-4" /> Messages
                      </Link>
                    </DropdownMenuItem>
                    {user.is_creator && (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="menu-dashboard">
                          <Store className="w-4 h-4" /> My Store
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/settings/notifications" className="flex items-center gap-2 cursor-pointer" data-testid="menu-notification-settings">
                        <Bell className="w-4 h-4" /> Notification Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-destructive cursor-pointer"
                      data-testid="menu-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            {!user && (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="lowercase font-display font-bold text-foreground hover:bg-foreground/10 hover:text-foreground rounded-full" data-testid="nav-login">login</Button>
                </Link>
                <Link to="/register">
                  <Button className="lowercase font-display font-bold bg-primary text-primary-foreground hover:bg-chilli-deep rounded-full px-6" data-testid="nav-register">join free</Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-foreground/10 hover:text-foreground" data-testid="nav-mobile-menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="flex flex-col gap-6 mt-8">
                  <NavLinks mobile />
                  {!user && (
                    <div className="flex flex-col gap-2 pt-4 border-t">
                      <Link to="/login" onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full lowercase font-display font-bold rounded-full">login</Button>
                      </Link>
                      <Link to="/register" onClick={() => setMobileOpen(false)}>
                        <Button className="w-full lowercase font-display font-bold rounded-full">join free</Button>
                      </Link>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
